// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris


export const OAUTH_POPUP_SOURCE = "riddle-oauth";

export type ConnectorId = "google" | "graph" | "docusign";

export type OAuthPopupResult =
  | { ok: true; accessToken?: string; connector?: ConnectorId }
  | { ok: false; error: string };

const OAUTH_TIMEOUT_MS = 120000;
const POLL_INTERVAL_MS = 300;
const CLOSED_GRACE_MS = 1500;
const STATUS_CHECK_INTERVAL_MS = 2000;

export interface AuthorizeWithPopupOptions {
  checkCompleted?: () => Promise<boolean>;
}

function isPopupClosed(popup: Window): boolean {
  try {
    return popup.closed;
  } catch {
    return false;
  }
}

const CONNECTORS: ConnectorId[] = ["google", "graph"];

interface PendingOAuthCompletion {
  resolve: (result: OAuthPopupResult) => void;
}

let pendingOAuthCompletion: PendingOAuthCompletion | null = null;
let messageListenerInstalled = false;

export const OAUTH_BROADCAST_CHANNEL = "riddle-oauth";

function openBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }
  try {
    return new BroadcastChannel(OAUTH_BROADCAST_CHANNEL);
  } catch {
    return null;
  }
}

function createWindowFeatures(): string {
  const width = 520;
  const height = 700;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);

  return `width=${width},height=${height},left=${Math.floor(left)},top=${Math.floor(top)},resizable=yes,scrollbars=yes`;
}

function parseSearchParams(href: string): URLSearchParams {
  const hashIndex = href.indexOf("#");
  const queryIndex = href.indexOf("?");
  if (queryIndex < 0) {
    return new URLSearchParams();
  }
  const end = hashIndex >= 0 && hashIndex > queryIndex ? hashIndex : href.length;
  return new URLSearchParams(href.slice(queryIndex + 1, end));
}

function parseHashParams(href: string): URLSearchParams | null {
  const hashIndex = href.indexOf("#");
  if (hashIndex < 0) {
    return null;
  }
  return new URLSearchParams(href.slice(hashIndex + 1));
}

export function tryParseOAuthResult(href: string): OAuthPopupResult | null {
  const query = parseSearchParams(href);

  const connectorMarker = query.get("connector");
  if (connectorMarker === "google" || connectorMarker === "graph" || connectorMarker === "docusign") {
    const status = query.get("status");
    if (status === "connected") {
      return { ok: true, connector: connectorMarker };
    }
    if (status === "error") {
      return { ok: false, error: query.get("reason") || "cancelled" };
    }
  }

  for (const connector of CONNECTORS) {
    const value = query.get(connector);
    if (value === "connected") {
      return { ok: true, connector };
    }
    if (value === "error") {
      return { ok: false, error: query.get("reason") || "cancelled" };
    }
  }

  const hash = parseHashParams(href);
  if (hash) {
    const error = hash.get("error");
    if (error !== null) {
      return { ok: false, error };
    }

    const accessToken = hash.get("access_token");
    if (accessToken && accessToken.length > 0) {
      return { ok: true, accessToken };
    }
  }

  return null;
}

function isOAuthPopupMessage(data: unknown): data is {
  source: string;
  connector?: ConnectorId;
  accessToken?: string;
  error?: string;
} {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { source?: unknown }).source === OAUTH_POPUP_SOURCE
  );
}

function handleOAuthResultMessage(event: MessageEvent<unknown>): void {
  if (event.origin !== window.location.origin) {
    return;
  }

  resolvePendingFromRelay(event.data);
}

function resolvePendingFromRelay(data: unknown): void {
  if (!isOAuthPopupMessage(data)) {
    return;
  }

  const pending = pendingOAuthCompletion;
  if (!pending) {
    return;
  }
  pendingOAuthCompletion = null;

  if (typeof data.error === "string" && data.error.length > 0) {
    pending.resolve({ ok: false, error: data.error });
  } else if (typeof data.accessToken === "string" && data.accessToken.length > 0) {
    pending.resolve({ ok: true, accessToken: data.accessToken });
  } else if (data.connector) {
    pending.resolve({ ok: true, connector: data.connector });
  }
}

function ensureMessageListener(): void {
  if (messageListenerInstalled) {
    return;
  }
  messageListenerInstalled = true;
  window.addEventListener("message", handleOAuthResultMessage);
}

export async function authorizeWithPopup(
  authorizeUrl: string,
  windowName: string,
  options: AuthorizeWithPopupOptions = {},
): Promise<OAuthPopupResult> {
  const popup = window.open(authorizeUrl, windowName, createWindowFeatures());
  if (popup === null) {
    throw new Error(
      "Unable to open sign-in window. Check popup permissions and try again.",
    );
  }

  ensureMessageListener();

  const startedAt = Date.now();
  let closedAt: number | null = null;
  let lastStatusCheckAt = startedAt;
  let statusCheckInFlight = false;

  const channel = openBroadcastChannel();

  const result = await new Promise<OAuthPopupResult>((resolve) => {
    pendingOAuthCompletion = { resolve };
    if (channel) {
      channel.onmessage = (event: MessageEvent<unknown>) => {
        resolvePendingFromRelay(event.data);
      };
    }

    const intervalId = window.setInterval(() => {
      if (!pendingOAuthCompletion) {
        window.clearInterval(intervalId);
        return;
      }

      if (
        options.checkCompleted &&
        !statusCheckInFlight &&
        Date.now() - lastStatusCheckAt >= STATUS_CHECK_INTERVAL_MS
      ) {
        lastStatusCheckAt = Date.now();
        statusCheckInFlight = true;
        options
          .checkCompleted()
          .then((done) => {
            if (done && pendingOAuthCompletion) {
              pendingOAuthCompletion = null;
              window.clearInterval(intervalId);
              try {
                popup.close();
              } catch {
              }
              resolve({ ok: true });
            }
          })
          .catch(() => undefined)
          .finally(() => {
            statusCheckInFlight = false;
          });
      }

      if (isPopupClosed(popup)) {
        closedAt ??= Date.now();
        if (Date.now() - closedAt < CLOSED_GRACE_MS) {
          return;
        }
        pendingOAuthCompletion = null;
        window.clearInterval(intervalId);
        resolve({ ok: false, error: "sign-in-window-closed" });
        return;
      }

      try {
        const parsed = tryParseOAuthResult(popup.location.href);
        if (parsed) {
          pendingOAuthCompletion = null;
          window.clearInterval(intervalId);
          popup.close();
          resolve(parsed);
          return;
        }
      } catch {
      }

      if (Date.now() - startedAt > OAUTH_TIMEOUT_MS) {
        pendingOAuthCompletion = null;
        popup.close();
        window.clearInterval(intervalId);
        resolve({ ok: false, error: "sign-in-timed-out" });
      }
    }, POLL_INTERVAL_MS);
  });

  channel?.close();
  return result;
}

export function broadcastRelayMessage(message: Record<string, unknown>): void {
  if ("accessToken" in message) {
    return;
  }
  const channel = openBroadcastChannel();
  if (!channel) {
    return;
  }
  try {
    channel.postMessage(message);
  } finally {
    channel.close();
  }
}

function toRelayMessage(result: OAuthPopupResult): Record<string, unknown> {
  if (result.ok) {
    return result.accessToken
      ? { source: OAUTH_POPUP_SOURCE, accessToken: result.accessToken }
      : { source: OAUTH_POPUP_SOURCE, connector: result.connector };
  }
  return { source: OAUTH_POPUP_SOURCE, error: result.error };
}

export function relayOAuthResultFromPopup(): void {
  const parsed = tryParseOAuthResult(window.location.href);
  if (!parsed) {
    return;
  }

  if (!window.opener) {
    broadcastRelayMessage(toRelayMessage(parsed));
    window.close();
    return;
  }

  /* istanbul ignore next */
  relayOAuthResultToOpener(
    window.opener,
    window.location.href,
    window.location.origin,
  );
}

export function relayOAuthResultToOpener(
  opener: { postMessage: (message: unknown, targetOrigin: string) => void },
  href: string,
  targetOrigin: string,
): void {
  const parsed = tryParseOAuthResult(href);
  if (!parsed) {
    return;
  }

  const message = toRelayMessage(parsed);
  opener.postMessage(message, targetOrigin);
  broadcastRelayMessage(message);
  window.close();
}
